import { Text, TextInput, IconButton, Add, Delete, IconButtonSize } from '@soteria/design-system-ve';
import { useState, useRef, useEffect, useCallback } from 'react';

import * as styles from './ClientIdListEditor.css';

interface ClientIdListEditorProps {
  /** Current list of client IDs */
  clientIds: string[];
  /** Whether inputs are disabled (e.g. during form submission) */
  disabled: boolean;
  /** Callback when the list changes — receives the full updated array */
  onChange: (ids: string[]) => void;
}

/**
 * Editable list of Application (Client) IDs with add/remove controls.
 * Extracts focus management and list manipulation from the parent dialog
 * to reduce complexity.
 */
export function ClientIdListEditor({ clientIds, disabled, onChange }: ClientIdListEditorProps) {
  const [focusedInputIndex, setFocusedInputIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Stable key generation — avoids both value-based collisions and index-based keys
  const keyCounterRef = useRef(0);
  const stableKeysRef = useRef<string[]>([]);

  // Synchronise stable keys with clientIds length
  if (stableKeysRef.current.length < clientIds.length) {
    // Items added — generate new keys for the additions
    while (stableKeysRef.current.length < clientIds.length) {
      stableKeysRef.current.push(`cid-${keyCounterRef.current++}`);
    }
  } else if (stableKeysRef.current.length > clientIds.length) {
    // Items removed — trim to match (removal is always by known index via handleRemoveClientId)
    stableKeysRef.current = stableKeysRef.current.slice(0, clientIds.length);
  }

  // Maintain focus after state updates using requestAnimationFrame (aligned with browser paint cycle)
  useEffect(() => {
    if (focusedInputIndex !== null && inputRefs.current[focusedInputIndex]) {
      requestAnimationFrame(() => {
        inputRefs.current[focusedInputIndex]?.focus();
      });
    }
  }, [clientIds, focusedInputIndex]);

  const handleClientIdChange = useCallback(
    (idIndex: number, value: string) => {
      setFocusedInputIndex(idIndex);

      const updated = [...clientIds];

      updated[idIndex] = value;
      onChange(updated);
    },
    [clientIds, onChange]
  );

  const handleAddClientId = useCallback(() => {
    onChange([...clientIds, '']);
  }, [clientIds, onChange]);

  const handleRemoveClientId = useCallback(
    (idIndex: number) => {
      // Remove the corresponding stable key to keep keys aligned
      stableKeysRef.current = stableKeysRef.current.filter((_, idx) => idx !== idIndex);
      onChange(clientIds.filter((_, idx) => idx !== idIndex));
    },
    [clientIds, onChange]
  );

  return (
    <>
      <div className={styles.sectionHeader}>
        <Text>Application (Client) IDs</Text>
      </div>
      {clientIds.map((clientId, idIndex) => {
        const canRemove = idIndex > 0;

        return (
          <div key={stableKeysRef.current[idIndex]} className={styles.clientIdContainer}>
            <TextInput
              ref={el => {
                inputRefs.current[idIndex] = el;
              }}
              required
              aria-label={`Client ID ${idIndex + 1}`}
              aria-required="true"
              autoFocus={idIndex === clientIds.length - 1 && idIndex > 0}
              className={styles.clientIdInput}
              disabled={disabled}
              id={`client-id-input-${idIndex}`}
              label={`Client ID ${idIndex + 1}`}
              placeholder="Client ID"
              value={clientId}
              onChange={value => handleClientIdChange(idIndex, value)}
              onFocus={() => setFocusedInputIndex(idIndex)}
            />
            <IconButton
              aria-label={canRemove ? `Remove client ID ${idIndex + 1}` : 'Add client ID'}
              buttonSize={IconButtonSize.Small}
              className={styles.iconButton}
              disabled={disabled}
              icon={canRemove ? Delete : Add}
              onClick={canRemove ? () => handleRemoveClientId(idIndex) : idIndex === 0 ? handleAddClientId : undefined}
            />
          </div>
        );
      })}
    </>
  );
}
